import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStrings } from '../../i18n';
import { adminAPI } from '../../services/adminService';
import { CartToastContainer, showCartToast } from '../../components/CartToast';

export default function AdminCustomers() {
  const strings = useStrings();
  const { getIdToken } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Move fetchCustomers above useEffect to fix no-use-before-define
  const fetchCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const idToken = await getIdToken();

      const params = new URLSearchParams({
        page,
        limit: 10,
        search: searchTerm
      });

      const response = await adminAPI.customers.list(params, idToken);

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      const data = await response.json();
      setCustomers(data.customers);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      setError(err.message);
      showCartToast(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [getIdToken, page, searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [page, searchTerm, fetchCustomers]);

  const handleViewDetails = async (customerId) => {
    try {
      const idToken = await getIdToken();
      const response = await adminAPI.customers.get(customerId, idToken);
      
      if (!response.ok) {
        throw new Error('Failed to fetch customer details');
      }
      
      const data = await response.json();
      setSelectedCustomer(data.user);
      setShowDetails(true);
    } catch (err) {
      showCartToast(`Error: ${err.message}`);
    }
  };

  return (
    <div className="w-full bg-primaryBg min-h-screen">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">
        {/* Back Button */}
        <Link to="/admin" className="inline-flex items-center text-secondary hover:text-secondary/80 mb-6 transition-colors">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {strings.admin?.backToDashboard || 'Back to Dashboard'}
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-heading text-headingText mb-2">
            {strings.admin?.customers || 'Customers'}
          </h1>
          <p className="text-bodyText">
            {strings.admin?.manageAllCustomers || 'View and manage all customer accounts'}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder={strings.admin?.searchCustomers || 'Search by email, name, phone...'}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              className="w-full px-4 py-3 border border-secondary/20 rounded-lg bg-surface text-headingText placeholder-textSecondary focus:outline-none focus:border-secondary"
            />
          </div>
        </div>

        {/* Customers Table */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-bodyText">{strings.admin?.loading || 'Loading...'}</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
          </div>
        ) : customers.length === 0 ? (
          <div className="bg-cardBg rounded-2xl shadow-card p-12 text-center border border-secondary/10">
            <p className="text-bodyText text-lg">{strings.admin?.noCustomers || 'No customers found'}</p>
          </div>
        ) : (
          <div className="bg-cardBg rounded-2xl shadow-card overflow-hidden border border-secondary/10">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface border-b border-secondary/20">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.name || 'Name'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.email || 'Email'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.phone || 'Phone'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.totalOrders || 'Orders'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.totalSpent || 'Total Spent'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.lastOrder || 'Last Order'}</th>
                    <th className="px-6 py-4 text-left text-sm font-heading text-headingText">{strings.admin?.actions || 'Actions'}</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.map((customer) => (
                    <tr key={customer._id} className="border-b border-secondary/10 hover:bg-surface transition-colors duration-200">
                      <td className="px-6 py-4 font-medium text-headingText">{customer.displayName}</td>
                      <td className="px-6 py-4 text-bodyText">{customer.email}</td>
                      <td className="px-6 py-4 text-bodyText">{customer.phone || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 rounded-full text-xs font-medium text-white bg-blue-600">
                          {customer.stats.totalOrders}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-medium text-headingText">
                        ₹{customer.stats.totalSpent?.toLocaleString('en-IN') || '0'}
                      </td>
                      <td className="px-6 py-4 text-sm text-bodyText">
                        {customer.stats.lastOrderDate 
                          ? new Date(customer.stats.lastOrderDate).toLocaleDateString()
                          : 'Never'}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleViewDetails(customer._id)}
                          className="px-3 py-1 text-sm text-secondary hover:bg-secondary/10 rounded-lg transition-colors duration-200"
                        >
                          {strings.admin?.view || 'View'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 bg-surface border-t border-secondary/20 flex items-center justify-center gap-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  ← {strings.admin?.previous || 'Previous'}
                </button>
                <span className="text-sm text-bodyText">
                  {strings.admin?.page || 'Page'} {page} {strings.admin?.of || 'of'} {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-bodyText hover:text-secondary disabled:opacity-50"
                >
                  {strings.admin?.next || 'Next'} →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Customer Details Modal */}
        {showDetails && selectedCustomer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-cardBg rounded-2xl shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto border border-secondary/10">
              <div className="p-8">
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                  <div>
                    <h2 className="text-2xl font-heading text-headingText mb-2">
                      {selectedCustomer.displayName}
                    </h2>
                    <p className="text-bodyText">{selectedCustomer.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedCustomer(null);
                    }}
                    className="text-2xl text-headingText hover:text-secondary transition-colors"
                  >
                    ✕
                  </button>
                </div>

                {/* Customer Info */}
                <div className="bg-surface rounded-lg p-6 mb-6 border border-secondary/10">
                  <h3 className="text-lg font-heading text-headingText mb-4">{strings.admin?.customerInfo || 'Customer Information'}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-textSecondary mb-1">{strings.admin?.email || 'Email'}</p>
                      <p className="text-bodyText">{selectedCustomer.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-textSecondary mb-1">{strings.admin?.phone || 'Phone'}</p>
                      <p className="text-bodyText">{selectedCustomer.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-textSecondary mb-1">{strings.admin?.createdAt || 'Member Since'}</p>
                      <p className="text-bodyText">{new Date(selectedCustomer.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-textSecondary mb-1">{strings.admin?.lastLogin || 'Last Login'}</p>
                      <p className="text-bodyText">{new Date(selectedCustomer.lastLoginAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-surface rounded-lg p-4 border border-secondary/10">
                    <p className="text-sm text-textSecondary mb-1">{strings.admin?.totalOrders || 'Total Orders'}</p>
                    <p className="text-2xl font-heading text-secondary">{selectedCustomer.stats.totalOrders}</p>
                  </div>
                  <div className="bg-surface rounded-lg p-4 border border-secondary/10">
                    <p className="text-sm text-textSecondary mb-1">{strings.admin?.totalSpent || 'Total Spent'}</p>
                    <p className="text-2xl font-heading text-secondary">₹{selectedCustomer.stats.totalSpent?.toLocaleString('en-IN') || '0'}</p>
                  </div>
                  <div className="bg-surface rounded-lg p-4 border border-secondary/10">
                    <p className="text-sm text-textSecondary mb-1">{strings.admin?.avgOrderValue || 'Avg Order'}</p>
                    <p className="text-2xl font-heading text-secondary">₹{selectedCustomer.stats.averageOrderValue?.toLocaleString('en-IN') || '0'}</p>
                  </div>
                  <div className="bg-surface rounded-lg p-4 border border-secondary/10">
                    <p className="text-sm text-textSecondary mb-1">{strings.admin?.memberStatus || 'Status'}</p>
                    <p className="text-sm font-medium text-green-600 mt-2">Active</p>
                  </div>
                </div>

                {/* Addresses */}
                {selectedCustomer.addresses && selectedCustomer.addresses.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-heading text-headingText mb-4">{strings.admin?.addresses || 'Addresses'}</h3>
                    <div className="space-y-4">
                      {selectedCustomer.addresses.map((addr, idx) => (
                        <div key={idx} className="bg-surface rounded-lg p-4 border border-secondary/10">
                          <div className="flex justify-between items-start mb-2">
                            <p className="font-medium text-headingText capitalize">{addr.type} {addr.isDefault && '(Default)'}</p>
                          </div>
                          <p className="text-sm text-bodyText">{addr.street}</p>
                          <p className="text-sm text-bodyText">{addr.city}, {addr.state} {addr.zipCode}</p>
                          <p className="text-sm text-bodyText">{addr.country}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Order History */}
                {selectedCustomer.orderHistory && selectedCustomer.orderHistory.length > 0 && (
                  <div>
                    <h3 className="text-lg font-heading text-headingText mb-4">{strings.admin?.orderHistory || 'Order History'}</h3>
                    <div className="space-y-2">
                      {selectedCustomer.orderHistory.slice(0, 5).map((order) => (
                        <div key={order._id} className="flex justify-between items-center p-3 bg-surface rounded-lg border border-secondary/10">
                          <div>
                            <p className="font-medium text-headingText">{order.orderId}</p>
                            <p className="text-xs text-bodyText">{new Date(order.createdAt).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-headingText">₹{order.amount.toLocaleString('en-IN')}</p>
                            <span className={`inline-block px-2 py-1 rounded text-xs font-medium text-white mt-1 ${
                              order.status === 'delivered' ? 'bg-green-600' :
                              order.status === 'shipped' ? 'bg-blue-600' :
                              'bg-yellow-600'
                            }`}>
                              {order.status.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-8 pt-6 border-t border-secondary/10">
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedCustomer(null);
                    }}
                    className="w-full px-6 py-3 bg-secondary text-white rounded-lg hover:bg-secondary/90 transition-colors duration-200 font-medium"
                  >
                    {strings.admin?.close || 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      <CartToastContainer />
      </div>
    </div>
  );
}
